import clsx from "clsx";
import { ArrowRight, ChevronLeft } from "lucide-react";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { filterXSS } from "xss";
import { ImportChoice } from "./Steps/ImportChoice";
import { ImportDetails } from "./Steps/ImportDetails";
import {
    ImportJobDetails,
    // ImportJobDetailsRef,
} from "./Steps/ImportJobDetails";
import { ImportLoading } from "./Steps/ImportLoading";
import { ImportTemplate } from "./Steps/ImportTemplate";
import { ImportValidateDetailsLoading } from "./Steps/ImportValidateDetailsLoading";
import aiApi from "@/api/ai";
import resumeApi from "@/api/resume";
import userJobApi from "@/api/userJob";
import { Button } from "@/components/Button/Button";
import { IconButton } from "@/components/Button/IconButton";
import { CreditSpendIndicator } from "@/components/Credits/CreditSpendIndicator";
import { Modal } from "@/components/Modal/Modal";
import { ImportJobDetailsLoading } from "@/components/Modals/NewResumeModal/Steps/ImportJobDetailsLoading";
import {
    ALLOWED_RESUME_FILE_TYPES,
    MAX_RESUME_FILE_SIZE,
    MIN_JD_CHARS,
    MIN_RESUME_TEXT_CHARS,
    NewResumeModalStep,
    JOB_DESCRIPTION_ERROR_MESSAGE,
} from "@/components/Modals/NewResumeModal/constants";
import { createNewResume } from "@/helper/createNewResume";
import { duplicateResume } from "@/helper/duplicateResume";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useDevice } from "@/hooks/useDevice";
import { useTransaction } from "@/hooks/useTransaction";
import { setShowImportFeedback } from "@/store/app/slice";
import { setImporterDebug } from "@/store/debug/slice";
import { getIsModalOpen, getModalProps } from "@/store/modal/selectors";
import { closeModal } from "@/store/modal/slice";
import { getResumeExamples } from "@/store/resume/selectors";
import { getTenantId } from "@/store/tenant/selectors";
import { getIsAdminUser, getUserDetails } from "@/store/user/selectors";
import { setUserCredits } from "@/store/user/slice";
import { updateUserJob } from "@/store/userJob/slice";
import { IStructuredJobData, IUserJob } from "@/types";
import { CreditActionCode } from "@/types/creditAction";
import { ModalTypes } from "@/types/modal";
import { getIsNonProductionEnv } from "@/utils/env";
import { trackPostHogEvent } from "@/utils/tracking";

const NewResumeModal = () => {
    const isOpen = useAppSelector(getIsModalOpen(ModalTypes.NEW_RESUME));
    const modalProps = useAppSelector(getModalProps(ModalTypes.NEW_RESUME));
    const [currentStep, setCurrentStep] = useState<NewResumeModalStep>(
        modalProps?.startStep || NewResumeModalStep.CHOICE,
    );
    const isAdmin = useAppSelector(getIsAdminUser);
    const isNonProd = getIsNonProductionEnv();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [importError, setImportError] = useState<string>("");
    const [jobInsights, setJobInsights] = useState<IStructuredJobData | null>(
        null,
    );
    const [inputType, setInputType] = useState<"file" | "text">("file");
    const [resumeInputText, setResumeInputText] = useState("");
    const [jobDescription, setJobDescription] = useState("");
    const resumeExamples = useAppSelector(getResumeExamples);
    const [selectedExample, setSelectedExample] = useState<string>("");
    const [jobTitle, setJobTitle] = useState("");
    const [isFileImport, setIsFileImport] = useState<boolean | null>(null);
    const { getCreditActionByCode, validateCreditAction } = useTransaction();
    const { isDesktop } = useDevice();
    const userDetails = useAppSelector(getUserDetails);
    const tenantId = useAppSelector(getTenantId);
    const [jobDescriptionError, setJobDescriptionError] = useState<string>("");
    const [resumeTextError, setResumeInputTextError] = useState<string>("");
    const [fileError, setFileError] = useState<string>("");
    const [showResumeInputWarning, setShowResumeInputWarning] =
        useState<boolean>(false);
    const [canBypassResumeInputWarning, setCanBypassResumeInputWarning] =
        useState<boolean>(false);
    const importResumeCreditAction = useMemo(
        () => getCreditActionByCode(CreditActionCode.IMPORT_RESUME),
        [getCreditActionByCode],
    );
    const [fileUploadedResumeText, setFileUploadedResumeText] =
        useState<string>("");
    const [isLoadingJobDetails, setIsLoadingJobDetails] =
        useState<boolean>(false);
    // const jobDetailsRef = useRef<ImportJobDetailsRef>(null);
    // const [activeJob, setActiveJob] = useState<IUserJob | undefined>(undefined);
    // const [activeJobDetailsTab, setActiveJobDetailsTab] = useState<
    //     "new" | "saved"
    // >("saved");

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (modalProps?.startStep) {
            setCurrentStep(modalProps.startStep);
        }
    }, [modalProps?.startStep]);

    const resetModal = useCallback(() => {
        setResumeInputText("");
        setJobDescription("");
        setJobTitle("");
        setSelectedFile(null);
        setSelectedExample("");
        setFileError("");
        setResumeInputTextError("");
        setJobDescriptionError("");
        setImportError("");
        setShowResumeInputWarning(false);
        setCanBypassResumeInputWarning(false);
        setFileUploadedResumeText("");
        setIsFileImport(null);
    }, []);

    const handleFileChange = (event?: React.ChangeEvent<HTMLInputElement>) => {
        setShowResumeInputWarning(false);
        setFileError("");
        setResumeInputTextError("");
        setFileUploadedResumeText("");

        if (!event) {
            setSelectedFile(null);
            return;
        }
        const file = event.target.files?.[0] || null;
        setFileError("");

        if (!file) {
            setSelectedFile(null);
            return;
        }

        if (!ALLOWED_RESUME_FILE_TYPES.includes(file.type)) {
            setFileError("Invalid file type. Please upload a PDF or DOCX file");
            return;
        }

        if (file.size > MAX_RESUME_FILE_SIZE) {
            setFileError("File size must be less than 10MB");
            return;
        }

        setSelectedFile(file);
    };

    const handleExampleChange = useCallback((exampleId: string | null) => {
        if (exampleId) {
            setSelectedExample(exampleId);
        } else {
            setSelectedExample("");
        }
    }, []);

    const handleBlankResumeSelect = useCallback(() => {
        setIsFileImport(false);
        dispatch(closeModal(ModalTypes.NEW_RESUME));
        trackPostHogEvent("new_resume_option_selected", {
            feature_name: "new_resume",
            option_selected: "create_from_blank",
        });
        createNewResume(navigate, {
            userDetails,
            isDesktop,
            userJobId: modalProps?.userJobId,
        });
    }, [dispatch, navigate, userDetails, isDesktop, modalProps?.userJobId]);

    const handleCreateFromTemplate = () => {
        setFileError("");
        setResumeInputTextError("");
        setJobDescriptionError("");
        setImportError("");
        setShowResumeInputWarning(false);

        const resumeExampleData = resumeExamples?.find(
            example => example.id === selectedExample,
        );
        if (resumeExampleData) {
            dispatch(closeModal(ModalTypes.NEW_RESUME));
            duplicateResume({
                navigate,
                resume: resumeExampleData,
                isExampleResume: true,
                isDesktop,
                userDetails,
                userJobId: modalProps?.userJobId,
                tenantId,
            });
        }
    };

    const handleOptionSelect = useCallback((importResume: boolean) => {
        setIsFileImport(importResume);
        resetModal();
        setCurrentStep(
            importResume
                ? NewResumeModalStep.RESUME_DETAILS
                : NewResumeModalStep.TEMPLATE,
        );
        trackPostHogEvent("new_resume_option_selected", {
            feature_name: "new_resume",
            option_selected: importResume
                ? "import_resume"
                : "create_from_template",
        });
    }, []);

    const handleJobDetailsSubmit = async (userJob: IUserJob) => {
        try {
            const isExistingJob = userJob.id !== "new";
            const jobToSave = isExistingJob
                ? userJob
                : {
                      ...userJob,
                      id: undefined,
                  };

            setIsLoadingJobDetails(true);
            const savedJob = await userJobApi.saveUserJob(
                jobToSave,
                isExistingJob ? userJob.id : undefined,
            );
            if (savedJob) {
                setCurrentStep(NewResumeModalStep.TEMPLATE);
                dispatch(updateUserJob(savedJob));
            }
        } catch (error) {
            setJobDescriptionError(JOB_DESCRIPTION_ERROR_MESSAGE);
        } finally {
            setIsLoadingJobDetails(false);
        }
    };

    const handleUploadJobDescription = async () => {
        if (!jobDescription) {
            // If no JD or insights, move to template as user doesn't want to apply JD
            setCurrentStep(NewResumeModalStep.TEMPLATE);
            return;
        }

        if (jobInsights) {
            // Already have insights, move to template
            setCurrentStep(NewResumeModalStep.TEMPLATE);
            return;
        }

        try {
            const sanitizedJobDescription = filterXSS(jobDescription, {
                whiteList: {},
                stripIgnoreTag: true,
                stripIgnoreTagBody: ["script", "style"],
                css: false,
            });
            const { jobInsights, isJobDescription } =
                await aiApi.getJobInsights({
                    jobDescription: sanitizedJobDescription,
                });

            if (isNonProd && isAdmin) {
                dispatch(
                    setImporterDebug({
                        jobInsights: JSON.stringify(jobInsights),
                        isJobDescription: isJobDescription ? "true" : "false",
                    }),
                );
            }
            if (isJobDescription) {
                setJobInsights(jobInsights);
                setCurrentStep(NewResumeModalStep.TEMPLATE);
            } else {
                setJobDescriptionError(JOB_DESCRIPTION_ERROR_MESSAGE);
                setCurrentStep(NewResumeModalStep.JOB_DETAILS);
            }
        } catch (error) {
            setJobDescriptionError(JOB_DESCRIPTION_ERROR_MESSAGE);
            setCurrentStep(NewResumeModalStep.JOB_DETAILS);
        } finally {
            setIsLoadingJobDetails(false);
        }
    };

    const handleNextStep = async () => {
        if (currentStep === NewResumeModalStep.RESUME_DETAILS) {
            if (inputType === "file" && selectedFile) {
                setCurrentStep(NewResumeModalStep.DETAILS_LOADING);
                handleUploadFile();
            } else if (inputType === "text" && resumeInputText?.trim()) {
                // If text input, go directly to JOB_DETAILS
                if (resumeInputText.length < MIN_RESUME_TEXT_CHARS) {
                    setResumeInputTextError(
                        `Resume must be at least ${MIN_RESUME_TEXT_CHARS} characters`,
                    );
                    return;
                }
                setCurrentStep(NewResumeModalStep.DETAILS_LOADING);
                handleUploadFile();
            }
        } else if (currentStep === NewResumeModalStep.JOB_DETAILS) {
            if (jobDescription && jobDescription.length < MIN_JD_CHARS) {
                setJobDescriptionError(
                    `Job description must be at least ${MIN_JD_CHARS} characters`,
                );
                return;
            }
            setCurrentStep(NewResumeModalStep.JOB_DETAILS_LOADING);
            handleUploadJobDescription();
            // setCurrentStep(NewResumeModalStep.TEMPLATE);

            // setIsLoadingJobDetails(true);
            // try {
            //     if (jobDetailsRef.current) {
            //         jobDetailsRef.current.submitForm();
            //     }
            // } finally {
            //     setIsLoadingJobDetails(false);
            // }
        } else if (currentStep === NewResumeModalStep.TEMPLATE) {
            if (importResumeCreditAction) {
                const { hasInsufficientFunds } = validateCreditAction({
                    actionCode: importResumeCreditAction.code,
                    amount: importResumeCreditAction.credits ?? 0,
                });
                if (hasInsufficientFunds) {
                    return;
                }
            }
            if (resumeInputText || fileUploadedResumeText) {
                handleImport();
            } else {
                handleCreateFromTemplate();
            }
        } else {
            setCurrentStep(prev => (prev + 1) as NewResumeModalStep);
        }
    };

    const handleUploadFile = async () => {
        const hasWarningVisible = showResumeInputWarning;
        setFileError("");
        setResumeInputTextError("");
        setShowResumeInputWarning(false);

        let formData;
        if (selectedFile) {
            formData = new FormData();
            formData.append("file", selectedFile);

            trackPostHogEvent("resume_file_upload_started", {
                feature_name: "new_resume",
                file_size: selectedFile.size,
            });
        }

        try {
            // If they are submitting the resume text whilst the warning is displayed, just let them go through.
            if (
                hasWarningVisible &&
                (resumeInputText || fileUploadedResumeText)
            ) {
                setCurrentStep(NewResumeModalStep.JOB_DETAILS);
                return;
            }

            const isFileUpload = inputType === "file";
            const response = await resumeApi.uploadResume(
                formData,
                isFileUpload ? undefined : resumeInputText,
            );
            // Validated text is now the cleaned raw resume text for future use.
            if (response.validatedText) {
                setFileUploadedResumeText(response.validatedText);
                if (isNonProd && isAdmin) {
                    dispatch(
                        setImporterDebug({
                            ...(response.debugDetails ?? {}),
                        }),
                    );
                }
            }

            if (response.showWarning) {
                setShowResumeInputWarning(true);
                setCanBypassResumeInputWarning(true);
                setCurrentStep(NewResumeModalStep.RESUME_DETAILS);
            } else {
                setCurrentStep(NewResumeModalStep.JOB_DETAILS);
            }
        } catch (err) {
            if (
                err instanceof Error &&
                err.message ===
                    "This doesn't appear to be a resume. Please try again."
            ) {
                setShowResumeInputWarning(true);
                setCanBypassResumeInputWarning(false);
            } else {
                setFileError("Failed to upload resume. Please try again.");
            }
            setCurrentStep(NewResumeModalStep.RESUME_DETAILS);
        }
    };

    const handleImport = async () => {
        setFileError("");
        setResumeInputTextError("");
        setJobDescriptionError("");
        setImportError("");
        setCurrentStep(NewResumeModalStep.IMPORT_LOADING);

        const resumeTextToUse =
            inputType === "file" ? fileUploadedResumeText : resumeInputText;

        try {
            // Sanitize resume text to prevent XSS attacks
            const sanitizedResumeText = filterXSS(resumeTextToUse, {
                whiteList: {},
                stripIgnoreTag: true,
                stripIgnoreTagBody: ["script", "style"],
                css: false,
            });

            const sanitizedJobTitle = filterXSS(jobTitle, {
                whiteList: {},
                stripIgnoreTag: true,
                stripIgnoreTagBody: ["script", "style"],
                css: false,
            });

            // TODO: Improve error handling here
            const response = await resumeApi.importResume({
                resumeText: sanitizedResumeText,
                jobInsights,
                templateId: selectedExample,
                jobTitle: sanitizedJobTitle,
            });
            const { resumeId, userUpdates, debugDetails } = response;

            if (resumeId) {
                dispatch(closeModal(ModalTypes.NEW_RESUME));
                if (debugDetails && isNonProd && isAdmin) {
                    dispatch(setImporterDebug(debugDetails));
                }
                dispatch(setShowImportFeedback(true));
                if (userUpdates.credits) {
                    dispatch(setUserCredits(userUpdates.credits));
                }

                trackPostHogEvent("resume_import_success", {
                    feature_name: "new_resume",
                    isDesktop,
                });

                const nextRoute = isDesktop
                    ? `/resume/${resumeId}`
                    : `/preview/${resumeId}`;
                navigate(nextRoute);
            }
        } catch (err) {
            setImportError("Failed to create resume. Please try again.");
            const errorMessage =
                err instanceof Error ? err.message : "Unknown error";
            trackPostHogEvent("resume_import_failed", {
                feature_name: "new_resume",
                error: errorMessage,
            });
        }
    };

    const handleResetModal = () => {
        // Only reset the modal if the user has actually closed the modal. If the modal is still present in the modals list, it means its just hidden for
        if (!isOpen) {
            setResumeInputText("");
            setJobDescription("");
            setJobTitle("");
            setSelectedFile(null);
            setSelectedExample("");
            setCurrentStep(NewResumeModalStep.CHOICE);
        }
    };

    const handleBackStep = () => {
        if (currentStep > NewResumeModalStep.CHOICE) {
            // If going back from DETAILS_LOADING, go to IMPORT
            if (currentStep === NewResumeModalStep.DETAILS_LOADING) {
                setCurrentStep(NewResumeModalStep.RESUME_DETAILS);
            }
            // If going back from JOB_DETAILS and we came from import flow
            else if (
                currentStep === NewResumeModalStep.JOB_DETAILS &&
                isFileImport
            ) {
                setCurrentStep(NewResumeModalStep.RESUME_DETAILS);
            } else if (
                currentStep === NewResumeModalStep.JOB_DETAILS &&
                !isFileImport
            ) {
                setCurrentStep(NewResumeModalStep.CHOICE);
            } else if (currentStep === NewResumeModalStep.TEMPLATE) {
                if (resumeInputText || fileUploadedResumeText) {
                    setCurrentStep(NewResumeModalStep.JOB_DETAILS);
                } else {
                    setCurrentStep(NewResumeModalStep.CHOICE);
                }
            } else {
                setCurrentStep(prev => (prev - 1) as NewResumeModalStep);
            }
        }
    };

    const getStepTitle = useCallback((step: NewResumeModalStep) => {
        switch (step) {
            case NewResumeModalStep.CHOICE:
                return "Create Your Resume";
            case NewResumeModalStep.RESUME_DETAILS:
                return "Add Your Resume";
            case NewResumeModalStep.JOB_DETAILS:
                return "Add Job Details";
            case NewResumeModalStep.TEMPLATE:
                return "Select a Template";
            case NewResumeModalStep.DETAILS_LOADING:
            case NewResumeModalStep.JOB_DETAILS_LOADING:
            case NewResumeModalStep.IMPORT_LOADING:
                return "";
        }
    }, []);

    const renderCurrentStep = () => {
        const stepContent = (() => {
            switch (currentStep) {
                case NewResumeModalStep.CHOICE:
                    return (
                        <ImportChoice
                            onOptionSelect={handleOptionSelect}
                            onBlankResumeSelect={handleBlankResumeSelect}
                        />
                    );
                case NewResumeModalStep.RESUME_DETAILS:
                    return (
                        <ImportDetails
                            inputType={inputType}
                            setInputType={(type: "file" | "text") => {
                                trackPostHogEvent(
                                    "resume_details_input_type_changed",
                                    {
                                        feature_name: "new_resume",
                                        input_type: type,
                                    },
                                );
                                setInputType(type);
                                setFileError("");
                                setResumeInputTextError("");
                            }}
                            selectedFile={selectedFile}
                            resumeText={resumeInputText}
                            jobTitle={jobTitle}
                            onFileChange={(
                                e?: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                                handleFileChange(e);
                            }}
                            onTextChange={(text: string) => {
                                setResumeInputText(text);
                                setResumeInputTextError("");
                                setShowResumeInputWarning(false);
                                setFileError("");
                            }}
                            onJobTitleChange={(title: string) => {
                                setJobTitle(title);
                                setResumeInputTextError("");
                                setFileError("");
                            }}
                            resumeTextError={resumeTextError}
                            fileError={fileError}
                            showResumeInputWarning={showResumeInputWarning}
                            onWarningBypass={() => {
                                trackPostHogEvent("new_resume_warning_bypass", {
                                    feature_name: "new_resume",
                                });
                                setShowResumeInputWarning(false);
                                handleNextStep();
                            }}
                            canBypassWarning={
                                showResumeInputWarning &&
                                canBypassResumeInputWarning &&
                                (resumeInputText.length >
                                    MIN_RESUME_TEXT_CHARS ||
                                    fileUploadedResumeText.length >
                                        MIN_RESUME_TEXT_CHARS)
                            }
                        />
                    );
                case NewResumeModalStep.DETAILS_LOADING:
                    return <ImportValidateDetailsLoading />;
                case NewResumeModalStep.JOB_DETAILS:
                    return (
                        <ImportJobDetails
                            jobDescription={jobDescription}
                            onJobDescriptionChange={(description: string) => {
                                setJobDescription(description);
                                setJobDescriptionError("");
                            }}
                            onNext={handleNextStep}
                            onJobInsightsChange={setJobInsights}
                            jobDescriptionError={jobDescriptionError}
                            // ref={jobDetailsRef}
                            // onSubmit={handleJobDetailsSubmit}
                            // onJobSelect={setActiveJob}
                            // activeJob={activeJob}
                            // activeTab={activeJobDetailsTab}
                            // onActiveTabChange={tab => {
                            //     setActiveJobDetailsTab(tab);
                            //     setJobDescriptionError("");
                            // }}
                        />
                    );
                case NewResumeModalStep.JOB_DETAILS_LOADING:
                    return <ImportJobDetailsLoading />;
                case NewResumeModalStep.TEMPLATE:
                    return (
                        <ImportTemplate
                            selectedExample={selectedExample}
                            resumeExamples={resumeExamples}
                            onExampleChange={handleExampleChange}
                            onNext={handleNextStep}
                            hasJobDescription={!!jobDescription || !!jobTitle}
                        />
                    );
                case NewResumeModalStep.IMPORT_LOADING:
                    return <ImportLoading importError={importError} />;
                default:
                    return null;
            }
        })();

        return (
            <div
                className={clsx(
                    "flex flex-col",
                    currentStep === NewResumeModalStep.DETAILS_LOADING &&
                        "h-full",
                    currentStep === NewResumeModalStep.IMPORT_LOADING &&
                        "h-full",
                    currentStep === NewResumeModalStep.JOB_DETAILS_LOADING &&
                        "h-full",
                )}
            >
                {stepContent}
            </div>
        );
    };

    const renderFooter = () => {
        switch (currentStep) {
            case NewResumeModalStep.CHOICE:
            case NewResumeModalStep.DETAILS_LOADING:
            case NewResumeModalStep.JOB_DETAILS_LOADING:
                return null;
            case NewResumeModalStep.IMPORT_LOADING:
                return importError ? (
                    <div className="flex justify-between">
                        <Button
                            variant="solid"
                            color="grey"
                            onClick={handleBackStep}
                            leftIcon={<ChevronLeft size={16} />}
                        >
                            Back
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={() => {
                                setImportError("");
                                handleImport();
                            }}
                        >
                            Retry
                        </Button>
                    </div>
                ) : null;
            case NewResumeModalStep.RESUME_DETAILS:
                return (
                    <div className="flex justify-between">
                        <Button
                            variant="solid"
                            color="grey"
                            onClick={handleBackStep}
                            leftIcon={<ChevronLeft size={16} />}
                        >
                            Back
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={handleNextStep}
                            disabled={
                                (!resumeInputText?.trim() && !selectedFile) ||
                                !!resumeTextError ||
                                !!fileError ||
                                showResumeInputWarning
                            }
                        >
                            Next
                        </Button>
                    </div>
                );
            case NewResumeModalStep.JOB_DETAILS:
                return (
                    <div className="flex justify-between">
                        <Button
                            variant="solid"
                            color="grey"
                            onClick={handleBackStep}
                            leftIcon={<ChevronLeft size={16} />}
                        >
                            Back
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={() => {
                                trackPostHogEvent(
                                    "new_resume_job_details_step",
                                    {
                                        feature_name: "new_resume",
                                        hasJobDescription: !!jobDescription,
                                    },
                                );
                                handleNextStep();
                            }}
                            // disabled={
                            //     isLoadingJobDetails ||
                            //     (activeJobDetailsTab === "saved" && !activeJob)
                            // }
                            disabled={!!jobDescriptionError}
                            loading={isLoadingJobDetails}
                        >
                            {/* {activeJobDetailsTab === "saved"
                                ? "Next - Choose Template"
                                : "Create Job"} */}
                            Next - Choose Template
                        </Button>
                    </div>
                );
            case NewResumeModalStep.TEMPLATE:
                return (
                    <div className="flex justify-between gap-3 pt-2">
                        <Button
                            variant="solid"
                            color="grey"
                            className="hidden lg:flex"
                            onClick={handleBackStep}
                            leftIcon={<ChevronLeft size={16} />}
                        >
                            Back
                        </Button>
                        <IconButton
                            variant="solid"
                            color="grey"
                            className="flex lg:hidden"
                            onClick={handleBackStep}
                        >
                            <ChevronLeft size={16} />
                        </IconButton>
                        <div className="flex items-center gap-3">
                            {resumeInputText || fileUploadedResumeText ? (
                                <CreditSpendIndicator
                                    creditAction={importResumeCreditAction}
                                    textClassName="text-right"
                                >
                                    <Button
                                        variant="solid"
                                        color="primary"
                                        onClick={handleNextStep}
                                        rightIcon={<ArrowRight size={16} />}
                                        disabled={!selectedExample}
                                    >
                                        Create Resume
                                    </Button>
                                </CreditSpendIndicator>
                            ) : (
                                <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={handleNextStep}
                                    rightIcon={<ArrowRight size={16} />}
                                    disabled={!selectedExample}
                                >
                                    Create Resume
                                </Button>
                            )}
                        </div>
                    </div>
                );
        }
    };

    return (
        <Modal
            open={isOpen}
            modalType={ModalTypes.NEW_RESUME}
            width="4xl"
            title={getStepTitle(currentStep)}
            footer={renderFooter()}
            closeOnOverlayClick={false}
            mobileFullScreen={true}
            gradientBackground={
                currentStep === NewResumeModalStep.DETAILS_LOADING ||
                currentStep === NewResumeModalStep.IMPORT_LOADING ||
                currentStep === NewResumeModalStep.JOB_DETAILS_LOADING
            }
            noHeader={
                currentStep === NewResumeModalStep.DETAILS_LOADING ||
                currentStep === NewResumeModalStep.IMPORT_LOADING ||
                currentStep === NewResumeModalStep.JOB_DETAILS_LOADING
            }
            disableScroll
            afterClose={handleResetModal}
        >
            {renderCurrentStep()}
        </Modal>
    );
};

export default NewResumeModal;
